// ─── Floor plan printing ────────────────────────────────────────
//
// One print pipeline for every floor plan surface: the producer page and the
// client portal both open FloorPlanPrintModal and hand the chosen options to
// printFloorPlan, which clones the live SVG and writes the print document.
// What varies per surface is only the metadata (event/venue/plan info,
// family-group colors) — never the layout of the printed page.

import { printDocument } from '../../utils/printDocument';

export interface FloorPlanPrintOptions {
  landscape: boolean;
  fitPage: boolean;
  pageSize: 'A4' | 'Letter' | 'Legal';
  includeInfo: boolean;
  familyColors: boolean;
}

export const DEFAULT_PRINT_OPTIONS: FloorPlanPrintOptions = {
  landscape: true,
  fitPage: false,
  pageSize: 'A4',
  includeInfo: true,
  familyColors: false,
};

export interface FloorPlanPrintMeta {
  eventName: string;
  /** Pre-formatted display date (e.g. "September 12, 2026"). */
  eventDate?: string | null;
  venueName?: string | null;
  roomLabel?: string | null;
  planName?: string | null;
  appLogoUrl?: string | null;
  tableCount: number;
  totalSeats: number;
  /** Omit (null) on surfaces without guest-assignment data (client portal). */
  personsAssigned?: number | null;
  familyGroupColors?: Record<string, string>;
}

export function formatPrintDate(date: string | Date | null | undefined): string | null {
  if (!date) return null;
  return new Date(date).toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'long',
    day: 'numeric',
  });
}

export function buildFloorPlanPrintHtml(
  svgContent: string,
  meta: FloorPlanPrintMeta,
  options: FloorPlanPrintOptions,
): string {
  const { landscape, fitPage, pageSize, includeInfo } = options;
  const withFamilyColors =
    options.familyColors && Object.keys(meta.familyGroupColors ?? {}).length > 0;

  const pageSizeStr = `${pageSize} ${landscape ? 'landscape' : 'portrait'}`;
  const pageStyle = `@page{size:${pageSizeStr};margin:0;}`;
  const colorAdjust = `*{print-color-adjust:exact;-webkit-print-color-adjust:exact;}`;
  const bodyStyle = fitPage
    ? `body{margin:0;padding:0;font-family:sans-serif;}` +
      `.print-page{padding:8mm 10mm;box-sizing:border-box;width:100%;min-height:100vh;height:100vh;display:flex;flex-direction:column;}` +
      // Compact single-row strip for fit-to-page
      `.ph{display:flex;align-items:center;gap:10px;padding-bottom:5px;margin-bottom:5px;border-bottom:1.5px solid #333;flex-shrink:0;}` +
      `.ph-logo{width:34px;height:34px;object-fit:contain;flex-shrink:0;}` +
      `.ph-title{font-size:14px;font-weight:700;color:#111;white-space:nowrap;margin-right:4px;}` +
      `.ph-meta{display:flex;flex-wrap:wrap;align-items:center;gap:0 10px;flex:1;font-size:10px;color:#555;}` +
      `.ph-meta span{white-space:nowrap;}` +
      `.ph-stats{font-size:10px;color:#374151;white-space:nowrap;margin-left:auto;font-weight:600;}`
    : `body{margin:0;padding:10mm;font-family:sans-serif;box-sizing:border-box;}` +
      `.ph{display:flex;align-items:flex-start;gap:16px;margin-bottom:12px;padding-bottom:10px;border-bottom:2px solid #333;flex-shrink:0;}` +
      `.ph-logo{width:80px;height:auto;object-fit:contain;flex-shrink:0;}` +
      `.ph-details{flex:1;}` +
      `.ph h1{margin:0 0 4px 0;font-size:20px;color:#111;}` +
      `.ph p{margin:2px 0;font-size:12px;color:#555;}`;
  const logoHtml = meta.appLogoUrl
    ? `<img src="${meta.appLogoUrl}" class="ph-logo" alt="NP Events &amp; Production" />`
    : '';

  const statsText =
    meta.tableCount > 0
      ? `Tables: ${meta.tableCount} &nbsp;·&nbsp; Seats: ${meta.totalSeats}` +
        (meta.personsAssigned != null ? ` &nbsp;·&nbsp; Assigned: ${meta.personsAssigned}` : '')
      : '';

  // Fit-to-page: compact horizontal strip (logo + title + meta pills + stats)
  const infoHtmlFit = includeInfo
    ? `<div class="ph">` +
      logoHtml +
      `<span class="ph-title">${meta.eventName}</span>` +
      `<div class="ph-meta">` +
      (meta.eventDate ? `<span>📅 ${meta.eventDate}</span>` : '') +
      (meta.venueName ? `<span>📍 ${meta.venueName}</span>` : '') +
      (meta.roomLabel ? `<span>🏠 ${meta.roomLabel}</span>` : '') +
      (meta.planName ? `<span>Plan: ${meta.planName}</span>` : '') +
      `</div>` +
      (statsText ? `<span class="ph-stats">${statsText}</span>` : '') +
      `</div>`
    : '';

  // Non-fit-to-page: original stacked layout
  const infoHtmlFull = includeInfo
    ? `<div class="ph">${logoHtml}<div class="ph-details"><h1>${meta.eventName}</h1>` +
      (meta.eventDate ? `<p>Date: ${meta.eventDate}</p>` : '') +
      (meta.venueName ? `<p>Venue: ${meta.venueName}</p>` : '') +
      (meta.roomLabel ? `<p>Room: ${meta.roomLabel}</p>` : '') +
      (meta.planName ? `<p>Floor Plan: ${meta.planName}</p>` : '') +
      (meta.tableCount > 0
        ? `<p>Tables: ${meta.tableCount} · Seats: ${meta.totalSeats}` +
          (meta.personsAssigned != null ? ` · Assigned: ${meta.personsAssigned}` : '') +
          `</p>`
        : '') +
      `</div></div>`
    : '';

  const infoHtml = fitPage ? infoHtmlFit : infoHtmlFull;

  const familyLegendHtml = withFamilyColors
    ? `<div class="family-legend">` +
      `<div class="family-legend-title">Family Groups</div>` +
      Object.entries(meta.familyGroupColors!)
        .map(
          ([group, color]) =>
            `<div class="family-legend-item"><svg width="14" height="14" style="flex-shrink:0;display:inline-block;vertical-align:middle"><rect width="14" height="14" rx="2" fill="${color}"/></svg><span>${group}</span></div>`,
        )
        .join('') +
      `</div>`
    : '';

  const fitPageLegendStyle = fitPage
    ? `.canvas-area{flex:1;min-height:0;display:flex;align-items:stretch;gap:12px;}` +
      `.svg-fill{flex:1;min-width:0;min-height:0;display:flex;align-items:center;justify-content:center;}` +
      `.svg-fill svg{width:100%;height:100%;}` +
      (withFamilyColors
        ? `.family-legend{flex-shrink:0;width:160px;border:1px solid #d1d5db;border-radius:4px;padding:8px 12px;background:white;}` +
          `.family-legend-title{font-weight:bold;font-size:11px;margin-bottom:6px;color:#374151;}` +
          `.family-legend-item{display:flex;align-items:center;gap:6px;font-size:10px;color:#374151;margin-bottom:4px;}`
        : '')
    : withFamilyColors
      ? `.content-row{display:flex;gap:16px;align-items:flex-start;}` +
        `.svg-wrap{flex:1;min-width:0;}` +
        `.family-legend{flex-shrink:0;width:160px;border:1px solid #d1d5db;border-radius:4px;padding:8px 12px;background:white;}` +
        `.family-legend-title{font-weight:bold;font-size:11px;margin-bottom:6px;color:#374151;}` +
        `.family-legend-item{display:flex;align-items:center;gap:6px;font-size:10px;color:#374151;margin-bottom:4px;}`
      : `.content-row{display:block;}.svg-wrap{}`;

  const containerHtml = fitPage
    ? `<div class="print-page">${infoHtml}<div class="canvas-area"><div class="svg-fill">${svgContent}</div>${familyLegendHtml}</div></div>`
    : `${infoHtml}<div class="content-row"><div class="svg-wrap">${svgContent}</div>${familyLegendHtml}</div>`;

  return (
    `<!DOCTYPE html><html><head><meta charset="UTF-8"><title>Floor Plan — ${meta.eventName}</title>` +
    `<style>${colorAdjust}${bodyStyle}${fitPageLegendStyle}${pageStyle}</style></head><body>` +
    containerHtml +
    `</body></html>`
  );
}

/**
 * Clone the live floor-plan SVG, size it for the chosen options, and open a
 * print window. `viewW`/`viewH` are the SVG's viewBox dimensions (after any
 * display rotation), used for the non-fit-to-page fixed sizing.
 */
export function printFloorPlan(
  svgEl: SVGElement | null,
  viewW: number,
  viewH: number,
  meta: FloorPlanPrintMeta,
  options: FloorPlanPrintOptions,
): void {
  if (!svgEl) return;

  const svgClone = svgEl.cloneNode(true) as SVGElement;
  if (options.fitPage) {
    svgClone.setAttribute('width', '100%');
    svgClone.setAttribute('height', '100%');
    svgClone.setAttribute('preserveAspectRatio', 'xMidYMid meet');
    svgClone.style.cssText = 'display:block;width:100%;height:100%;';
  } else {
    svgClone.setAttribute('width', String(viewW));
    svgClone.setAttribute('height', String(viewH));
  }

  // Remove the SVG-embedded family legend from the clone — the print HTML
  // already includes a dedicated HTML legend, so keeping both would duplicate it.
  if (options.familyColors) {
    const svgLegend = svgClone.querySelector('[data-testid="family-legend"]');
    svgLegend?.parentNode?.removeChild(svgLegend);
  }

  printDocument(buildFloorPlanPrintHtml(svgClone.outerHTML, meta, options), {
    width: 1200,
    height: 900,
  });
}
